import React, { useState, useMemo } from 'react';
import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import {useDispatch, useSelector} from "react-redux";
import {makeStyles, createStyles} from "@material-ui/core/styles";

import GlbReceitasFilterBar from "./GlbReceitasFilterBar";
import GlbReceitaGroupRow from "./GlbReceitaGroupRow";
import {getGlbReceitas, patchGlbReceitaEhAtivo} from "../../../actions/raspaRecipeActions";

const useStyles = makeStyles((theme) =>
    createStyles({
        root: {
            padding: theme.spacing(2),
        },
        title: {
            marginBottom: theme.spacing(2),
        },
        noResults: {
            textAlign: 'center',
            padding: theme.spacing(4),
            color: '#999',
        },
        resultsSummary: {
            marginBottom: theme.spacing(1),
            color: '#666',
            fontSize: '0.85rem',
        },
    })
);

const initialFilters = {
    categoriaId: null,
    fabricanteId: null,
    modeloId: null,
    medidaId: null,
    bandaId: null,
    vidaPneuRaspagem: '',
};

// Agrupar por chave: glbPneuModeloId_glbPneuMedidaId_glbBandaId_larguraBanda_vidaPneuRaspagem
const groupRecipes = (receitas) => {
    const groups = {};
    receitas.forEach(r => {
        const key = `${r.glbPneuModeloId || ''}_${r.glbPneuMedidaId || ''}_${r.glbBandaId || ''}_${r.larguraBanda || ''}_${r.vidaPneuRaspagem || ''}`;
        if (!groups[key]) groups[key] = [];
        groups[key].push(r);
    });
    return groups;
};

const GlbReceitasView = ({ categoriasPermitidas }) => {
    const classes = useStyles();
    const dispatch = useDispatch();
    const [filters, setFilters] = useState(initialFilters);
    const [hasSearched, setHasSearched] = useState(false);

    // Redux state
    const glbReceitas = useSelector((state) => state.raspaRecipes.GlbReceitas);
    const isFetching = useSelector((state) => state.raspaRecipes.isFetchingGlbReceitas);
    const allCategorias = useSelector((state) => state.raspaRecipes.GlbReceitaCategorias);
    const glbModelos = useSelector((state) => state.raspaRecipes.GlbPneuModelos);
    const glbMedidas = useSelector((state) => state.raspaRecipes.GlbPneuMedidas);
    const glbBandas = useSelector((state) => state.raspaRecipes.GlbBandas);

    // Filtrar categorias pelas permitidas (null = todas)
    const categorias = useMemo(() => {
        if (!allCategorias) return [];
        if (!categoriasPermitidas) return allCategorias;
        return allCategorias.filter(c => categoriasPermitidas.includes(c.id));
    }, [allCategorias, categoriasPermitidas]);

    const grouped = useMemo(() => groupRecipes(glbReceitas || []), [glbReceitas]);
    const groupKeys = Object.keys(grouped);

    const handleFilterChange = (field, value) => {
        setFilters(prev => {
            const next = { ...prev, [field]: value };
            // Cascade: reset modelo when fabricante changes
            if (field === 'fabricanteId') {
                next.modeloId = null;
            }
            return next;
        });
    };

    const handleSearch = () => {
        setHasSearched(true);
        // Se tem fabricante mas nao modelo, calcular IDs de modelos do fabricante
        // para enviar como filtro inq ao backend
        const searchFilters = { ...filters };
        if (filters.fabricanteId && !filters.modeloId && glbModelos) {
            searchFilters.glbModeloIdsByFabricante = glbModelos
                .filter(m => m.glbFabricanteId === filters.fabricanteId)
                .map(m => m.id);
        }
        dispatch(getGlbReceitas(searchFilters));
    };

    const handleToggleAtivo = (id, newValue) => {
        dispatch(patchGlbReceitaEhAtivo(id, newValue));
    };

    return (
        <Grid container className={classes.root}>
            <Grid item xs={12}>
                <Typography variant="h6" className={classes.title}>
                    Receitas Globais
                </Typography>
            </Grid>

            <Grid item xs={12}>
                <GlbReceitasFilterBar
                    categorias={categorias || []}
                    glbModelos={glbModelos || []}
                    glbMedidas={glbMedidas || []}
                    glbBandas={glbBandas || []}
                    filters={filters}
                    onFilterChange={handleFilterChange}
                    onSearch={handleSearch}
                    isLoading={isFetching}
                />
            </Grid>

            <Grid item xs={12}>
                {hasSearched && !isFetching && glbReceitas && glbReceitas.length > 0 && (
                    <Typography className={classes.resultsSummary}>
                        {glbReceitas.length} receita{glbReceitas.length !== 1 ? 's' : ''} encontrada{glbReceitas.length !== 1 ? 's' : ''} em {groupKeys.length} grupo{groupKeys.length !== 1 ? 's' : ''}
                    </Typography>
                )}

                {hasSearched && !isFetching && (!glbReceitas || glbReceitas.length === 0) && (
                    <Typography className={classes.noResults}>
                        Nenhuma receita encontrada para os filtros selecionados.
                    </Typography>
                )}

                {groupKeys.map(key => (
                    <GlbReceitaGroupRow
                        key={key}
                        groupKey={key}
                        receitas={grouped[key]}
                        modelos={glbModelos || []}
                        medidas={glbMedidas || []}
                        bandas={glbBandas || []}
                        onToggleAtivo={handleToggleAtivo}
                    />
                ))}
            </Grid>
        </Grid>
    );
};

export default GlbReceitasView;
